import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useHeatmapEvents, useFeatureFlag, bucketEvents, type HeatPoint } from "@/lib/analytics";
import { HeatmapLayer } from "@/components/HeatmapLayer";
import { Locked } from "./analytics.index";
import { Activity, EyeOff } from "lucide-react";

export const Route = createFileRoute("/_authenticated/analytics/activity")({
  component: ActivityDensityPage,
});

function ActivityDensityPage() {
  const enabled = useFeatureFlag("advanced_analytics");
  const since = useMemo(() => new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString(), []);
  const { data: events = [] } = useHeatmapEvents({
    sources: ["gps", "scan"],
    fromDate: since,
    limit: 8000,
  });

  const points: HeatPoint[] = events.map((e) => ({
    lat: Number(e.latitude), lng: Number(e.longitude), weight: Number(e.weight) || 1,
  }));

  // Blind spot detection: tour checkpoints with low scan density nearby would
  // require joining tour_checkpoints; here we surface low-density buckets as proxies.
  const buckets = bucketEvents(events, 3);
  const cold = [...buckets].sort((a, b) => a.event_count - b.event_count).slice(0, 5);

  if (!enabled.isLoading && !enabled.data) return <Locked />;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-4 text-sm">
        <Activity className="mr-2 inline h-4 w-4 text-success" />
        Patrol coverage density · last 7 days · <b>{events.length.toLocaleString()}</b> movement events
      </div>

      <HeatmapLayer points={points} height={500} radius={32} intensity={1.2} />

      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="font-display text-lg font-semibold">
          <EyeOff className="mr-2 inline h-4 w-4 text-warning" />
          Coldest patrol zones
        </h3>
        <p className="text-sm text-muted-foreground">Geo buckets with the fewest movement events — possible blind spots.</p>
        {cold.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Need more patrol data to detect blind spots.</p>
        ) : (
          <ul className="mt-4 divide-y divide-border">
            {cold.map((c) => (
              <li key={`${c.bucket_lat}-${c.bucket_lng}`} className="flex items-center justify-between py-2 text-sm">
                <span className="font-mono text-xs text-muted-foreground">{c.bucket_lat.toFixed(3)}, {c.bucket_lng.toFixed(3)}</span>
                <span>{c.event_count} events</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
